import { useState, useEffect, useRef } from 'react';
import { useStore } from '@services/store';

export const PlayerNotConnected = () => {
  const { session } = useStore();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Ajuster la hauteur du widget Crisp
  useEffect(() => {
    const adjustHeight = () => {
      if (containerRef.current && (window as any).$crisp?.setHeight) {
        const height = containerRef.current.scrollHeight;
        console.log('🎨 Setting Crisp widget height to:', height);
        (window as any).$crisp.setHeight(Math.min(height + 40, 600));
      }
    };

    // Ajuster après un court délai pour laisser le DOM se rendre
    const timer = setTimeout(adjustHeight, 100);

    // Réajuster si le contenu change (erreur, succès, etc.)
    adjustHeight();

    return () => clearTimeout(timer);
  }, [sent, error]);

  const handleSendMessage = async () => {
    if (!session?.sessionId || !session?.websiteId) {
      setError('Session non trouvée');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const response = await fetch('/api/send-connection-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: session.sessionId,
          website_id: session.websiteId,
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi du message');
      }

      setSent(true);

      // Réinitialiser après 3 secondes pour permettre de renvoyer si nécessaire
      setTimeout(() => {
        setSent(false);
      }, 3000);
    } catch (err) {
      console.error('Error sending connection request:', err);
      setError('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div ref={containerRef} className="w-full bg-white py-8 px-6">
      <div className="max-w-md mx-auto">
        {/* Icon */}
        <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-4xl">⚠️</span>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
          Joueur non connecté
        </h1>

        {/* Description */}
        <p className="text-gray-600 text-center mb-6">
          Le joueur n'est pas actuellement connecté au serveur. Vous pouvez lui envoyer un message pour lui demander de se connecter.
        </p>

        {/* Success Message */}
        {sent && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-green-600 text-lg">✓</span>
              <p className="text-sm text-green-800 font-medium">
                Message envoyé avec succès !
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-red-600 text-lg">⚠</span>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handleSendMessage}
          disabled={sending || sent}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? (
            <>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              <span>Envoi en cours...</span>
            </>
          ) : sent ? (
            <>
              <span className="text-2xl">✓</span>
              <span>Message envoyé</span>
            </>
          ) : (
            <>
              <span className="text-2xl">💬</span>
              <span>Demander la connexion du joueur</span>
            </>
          )}
        </button>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-sm text-blue-900">
            <strong>Message qui sera envoyé :</strong>
          </p>
          <p className="text-sm text-blue-800 mt-2 italic">
            "L'opérateur support a besoin que vous vous connectiez pour gérer ce ticket, une popup va s'ouvrir pour vous permettre la connexion 🔐, si ce n'est pas le cas, nous vous invitons à vous connecter depuis le bouton connexion situé en haut à droite du site"
          </p>
        </div>
      </div>
    </div>
  );
};
